import { useMemo, useState } from 'react';

import { InstantReflex, InstantReflexVersionRef } from 'shiftNotes/_types';
import { useInstantReflex } from 'shared/hooks/api/shiftNotes';

import { sortSteps } from './helpers';

interface UseReflexVersionsArgs {
  instantReflex: InstantReflex;
  noteInstantReflexes?: InstantReflex[];
}

export default function useReflexVersions({
  instantReflex,
  noteInstantReflexes,
}: UseReflexVersionsArgs) {
  const latestVersion = instantReflex.version ?? 1;
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );

  const versions = useMemo<InstantReflexVersionRef[]>(() => {
    if (instantReflex.versions) return instantReflex.versions;
    if (noteInstantReflexes && noteInstantReflexes.length > 0) {
      return [...noteInstantReflexes]
        .map((reflex) => ({
          id: reflex.id,
          version: reflex.version ?? 1,
          created: reflex.created,
        }))
        .sort((a, b) => b.version - a.version);
    }
    return [{ id: instantReflex.id, version: latestVersion, created: '' }];
  }, [
    instantReflex.versions,
    instantReflex.id,
    latestVersion,
    noteInstantReflexes,
  ]);

  const isViewingOldVersion =
    selectedVersionId !== null && selectedVersionId !== instantReflex.id;
  const { data: oldReflex } = useInstantReflex(
    selectedVersionId ?? '',
    isViewingOldVersion
  );

  const activeReflex = isViewingOldVersion ? oldReflex : instantReflex;
  const isLoadingVersion = isViewingOldVersion && !oldReflex;

  const latestSections = useMemo(
    () => sortSteps(instantReflex.steps),
    [instantReflex.steps]
  );
  const { answerSteps, guidanceSteps } = useMemo(
    () => (isViewingOldVersion ? sortSteps(oldReflex?.steps) : latestSections),
    [isViewingOldVersion, oldReflex?.steps, latestSections]
  );
  const ratedSteps = useMemo(
    () => [...answerSteps, ...guidanceSteps],
    [answerSteps, guidanceSteps]
  );

  const isEmptyReflex =
    latestSections.answerSteps.length + latestSections.guidanceSteps.length ===
    0;

  const selectedVersionRef = versions.find(
    (version) => version.id === (selectedVersionId ?? instantReflex.id)
  );

  return {
    latestVersion,
    versions,
    selectedVersionId,
    setSelectedVersionId,
    selectedVersionRef,
    isViewingOldVersion,
    activeReflex,
    isLoadingVersion,
    answerSteps,
    guidanceSteps,
    ratedSteps,
    isEmptyReflex,
  };
}
